import { useEffect, useMemo, useState } from 'react';
import { api, ConnectionsRequiredError, type FactorySummary } from '../api.ts';
import {
  draftToWorkItem,
  newWorkItemId,
  nextWorkItemId,
  orderRepos,
  type WorkItemDraft,
} from '../views/FactoryWorkflowsView.tsx';

/**
 * Describe a piece of work and start it down a factory.
 *
 * The form asks for what a person knows and reef does not — what happened,
 * and where — and files it at the first stage of the chosen factory. The id
 * is minted, the priority is triage's to set, and the arrival state is the
 * control plane's; asking for any of those would be asking the person to do
 * a machine's job before they may describe their problem.
 *
 * The factory picker lists only delivery factories with a deployed first
 * stage. A factory whose entry is not deployed here has no door, and offering
 * it would start a run that fails at preflight.
 *
 * With `workflowId` given there is no picker: the form files at that stage
 * and says so as a fact. That is the factory detail's "Start this stage" —
 * the person has already chosen where the work goes by opening the stage,
 * and a dropdown defaulting to the front door would quietly file it at triage
 * instead.
 */
export function NewWorkItem({
  factories,
  initialFactoryId,
  workflowId,
  onStarted,
  onCancel,
}: {
  factories: readonly FactorySummary[];
  initialFactoryId?: string | null;
  /** Fix the stage instead of offering the factory picker. */
  workflowId?: string | null;
  onStarted(runId: string, workflowId: string): void;
  onCancel?(): void;
}) {
  const doors = useMemo(
    () => factories.filter((factory) => factory.kind === 'delivery' && factory.stages[0]?.present),
    [factories],
  );
  const [factoryId, setFactoryId] = useState<string>(
    initialFactoryId && doors.some((door) => door.id === initialFactoryId)
      ? initialFactoryId
      : (doors[0]?.id ?? ''),
  );
  const [draft, setDraft] = useState<WorkItemDraft>({ title: '', description: '', repo: '' });
  const [workItemId, setWorkItemId] = useState(newWorkItemId);
  const [repos, setRepos] = useState<string[]>([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .factoryRepos()
      .then((result) => current && setRepos(orderRepos(result.repos)))
      .catch(() => current && setRepos([]));
    return () => {
      current = false;
    };
  }, []);

  /*
   * Derived rather than remembered. The factory list arrives after this form
   * mounts when it is opened from a deep link, so a choice recorded at mount
   * would be empty forever and the button would stay disabled with nothing on
   * screen to say why. The first door stands in until a person picks one.
   */
  const factory = doors.find((door) => door.id === factoryId) ?? doors[0] ?? null;
  /*
   * A fixed stage is looked up in the factory it was opened from first: triage
   * is a stage of three factories, and naming the wrong one under "Starts at"
   * would tell the person their work is entering a lifecycle it is not. The
   * workflow id alone stands in when no factory lists it, so the form still
   * files rather than going blank.
   */
  const fixed = workflowId
    ? (
        [
          ...factories.filter((candidate) => candidate.id === initialFactoryId),
          ...factories,
        ]
          .flatMap((candidate) => candidate.stages)
          .find((candidate) => candidate.workflowId === workflowId) ?? {
          name: workflowId,
          workflowId,
        }
      )
    : null;
  const entry = fixed ?? factory?.stages[0] ?? null;
  const valid = draft.title.trim().length > 0 && entry !== null;

  const start = async () => {
    if (!entry) return;
    setStarting(true);
    setError(null);
    let outcome: 'started' | 'failed' = 'failed';
    try {
      const result = await api.runWorkflow(
        entry.workflowId,
        `${draft.title.trim()} — filed from the studio`,
        draftToWorkItem(draft, workItemId),
      );
      outcome = 'started';
      onStarted(result.runId, entry.workflowId);
    } catch (caught) {
      setError(
        caught instanceof ConnectionsRequiredError
          ? `${caught.message} ${caught.missing.map((missing) => missing.connectionName).join(', ')}`
          : (caught as Error).message,
      );
    } finally {
      // A refused start keeps its id — it is the same item — and a started one
      // rotates, so the next thing typed here is a different piece of work.
      setWorkItemId((current) => nextWorkItemId(current, outcome));
      setStarting(false);
    }
  };

  // A fixed stage has its door: the empty state below is about having no
  // factory to choose, and there is nothing to choose here.
  if (doors.length === 0 && !fixed) {
    return (
      <div className="empty-state">
        <h3>No factory has a door open</h3>
        <p>
          A work item enters through a delivery factory's first stage, and there is none deployed
          here. A factory is committed as a <code>.ts</code> in <code>factories/</code> and listed
          in <code>factories/index.ts</code>; its first stage needs a workflow this deployment has.
        </p>
      </div>
    );
  }

  return (
    <form
      className="new-item"
      onSubmit={(event) => {
        event.preventDefault();
        void start();
      }}
    >
      {fixed ? (
        <p className="new-item-fact hint">
          Starts at <strong>{fixed.name}</strong> · <code>{fixed.workflowId}</code>
        </p>
      ) : (
        <label>
          <span>Factory</span>
          <select value={factory?.id ?? ''} onChange={(event) => setFactoryId(event.target.value)}>
            {doors.map((door) => (
              <option key={door.id} value={door.id}>
                {door.name}
              </option>
            ))}
          </select>
          {entry && (
            <small className="hint">
              Starts at <strong>{entry.name}</strong> · {entry.workflowId}
            </small>
          )}
        </label>
      )}

      <label>
        <span>
          Title <span className="required">*</span>
        </span>
        <input
          value={draft.title}
          placeholder="Checkout 500s when a promo code is applied"
          onChange={(event) => setDraft({ ...draft, title: event.target.value })}
        />
      </label>

      <label>
        <span>What happened</span>
        <textarea
          rows={4}
          value={draft.description}
          placeholder="What you saw, where, and what you expected instead. Paste the error if there is one."
          onChange={(event) => setDraft({ ...draft, description: event.target.value })}
        />
      </label>

      <label>
        <span>Repository</span>
        <select value={draft.repo} onChange={(event) => setDraft({ ...draft, repo: event.target.value })}>
          <option value="">Let triage decide</option>
          {repos.map((repo) => (
            <option key={repo} value={repo}>
              {repo}
            </option>
          ))}
        </select>
      </label>

      {error && <div className="banner danger">{error}</div>}

      <div className="row">
        <button type="submit" className="primary" disabled={!valid || starting}>
          {starting ? 'Starting…' : 'Start the work'}
        </button>
        {onCancel && (
          <button type="button" className="ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        <span className="spacer" />
        <span className="hint">
          filed as <code>{workItemId}</code>
        </span>
      </div>
    </form>
  );
}
